import type { ExportModel } from './build-export-model.js';
import type { TilePage } from './compute-tile-pages.js';
import {
  CSS_PX_PER_PT,
  DEFAULT_COLUMN_WIDTH_PX,
  DEFAULT_ROW_HEIGHT_PX,
  HEADER_ROW_RESERVED_PX,
  ROW_LABEL_WIDTH_PX,
  SUBJECT_DESCRIPTION_RESERVED_PX,
} from './page-geometry.js';

/**
 * Where each cell sits on a rendered PDF page — decision S5 (2026-08-27 doc).
 *
 * This is what lets a stroke drawn *on the PDF* be stored against matrix
 * content: given a point the viewer reports, we can say which cell it fell on
 * and where inside it, and the existing anchor model (A1-A3) takes over from
 * there.
 *
 * It is exact rather than approximate only because every vertical reservation
 * is **pinned** in the export stylesheet — `render-export-html.ts` fixes the
 * document header, the optional subject block and the column-header row to the
 * very constants `computeTilePages` budgets with. Any reservation left as a
 * conservative guess would show up as drift between where a stroke was drawn
 * and where it is baked.
 */

/**
 * The layout is authored in CSS pixels at 96/inch; a PDF is measured in points
 * at 72/inch. Chromium's print pass applies this factor, so every conversion
 * between the two spaces goes through it.
 *
 * Same ratio as `CSS_PX_PER_PT` and now an alias of it rather than a second
 * literal — one name reads correctly at a font-size call site, the other at a
 * page-coordinate one, but a codebase cannot afford two constants that must
 * never disagree.
 */
export const CSS_PX_PER_PDF_POINT = CSS_PX_PER_PT;

export interface PageColumnBox {
  number: number;
  /** Offset from the content area's left edge, in unscaled CSS pixels. */
  leftPx: number;
  widthPx: number;
}

export interface PageRowBox {
  key: string;
  /** Offset from the content area's top edge, in unscaled CSS pixels. */
  topPx: number;
  heightPx: number;
}

export interface PageLayout {
  /** Left edge of the first content column, from the page's left edge. */
  contentLeftPx: number;
  /** Top edge of the first body row, from the page's top edge. */
  contentTopPx: number;
  columns: PageColumnBox[];
  rows: PageRowBox[];
  /** The tile's uniform scale, so callers can convert stroke paths too. */
  scalePercent: number;
}

export interface CellAnchorPoint {
  rowKey: string;
  columnNumber: number;
  originX: number;
  originY: number;
}

/**
 * Builds the box model of one rendered page.
 *
 * Sizes here are *unscaled* while offsets are scaled, because the tile's scale
 * shrinks the rendered grid but not the page margins: mixing the two is the
 * easiest way to get this subtly wrong, so each is applied explicitly.
 */
export function buildPageLayout(input: {
  tile: TilePage;
  model: ExportModel;
  columnWidths: Record<string, number>;
  rowHeights: Record<string, number>;
  labelColumnCount: 1 | 2;
  /** Physical page margin, in unscaled CSS px — `PageGeometry.marginPx` for whatever format/margins preset this document was laid out with. */
  marginPx: number;
  /**
   * The document header's actual rendered height, in unscaled CSS px —
   * `documentHeaderHeightPx(...)`'s own return value, computed from the same
   * title/logo/dates inputs the document header itself renders with. Charged
   * to the first page in full, and to any later page on the top row-strip
   * past the first column-page (the reserved band — see `bandPx` below),
   * mirroring the reservation `render-export-html.ts` makes for the same two
   * cases.
   */
  documentHeaderHeightPx: number;
  /** The document header renders once, so only the first page pays for it. */
  isFirstPage: boolean;
  subjectDescriptionIncluded: boolean;
}): PageLayout {
  const { tile, model, columnWidths, rowHeights, labelColumnCount } = input;
  const scale = tile.scalePercent / 100;

  const columns: PageColumnBox[] = [];
  let left = 0;
  for (const index of tile.columnIndices) {
    const column = model.columns[index];
    if (column === undefined) continue;
    const widthPx = columnWidths[String(column.number)] ?? DEFAULT_COLUMN_WIDTH_PX;
    columns.push({ number: column.number, leftPx: left, widthPx });
    left += widthPx;
  }

  const rows: PageRowBox[] = [];
  let top = 0;
  for (const index of tile.rowIndices) {
    const row = model.rows[index];
    if (row === undefined) continue;
    const heightPx = rowHeights[`${row.kind}:${row.key}`] ?? DEFAULT_ROW_HEIGHT_PX;
    rows.push({ key: row.key, topPx: top, heightPx });
    top += heightPx;
  }

  // The document header and subject block sit above the whole tiled grid and
  // render only on the first page. Every other page on the top row-strip past
  // the first column-page reserves an empty band of the same header height so
  // that a horizontal join keeps every grid row at the same Y — the same band
  // `render-export-html.ts`'s `renderTile` draws (spec D3). A page on any
  // later row-strip reserves neither: the header cannot reach it, and the
  // band only exists to align a *column* join.
  const bandPx =
    tile.rowPageIndex === 0 && tile.columnPageIndex > 0 ? input.documentHeaderHeightPx : 0;
  const documentChromePx = input.isFirstPage
    ? input.documentHeaderHeightPx +
      (input.subjectDescriptionIncluded ? SUBJECT_DESCRIPTION_RESERVED_PX : 0)
    : bandPx;

  return {
    contentLeftPx: input.marginPx + labelColumnCount * Math.round(ROW_LABEL_WIDTH_PX * scale),
    contentTopPx: input.marginPx + documentChromePx + Math.round(HEADER_ROW_RESERVED_PX * scale),
    columns,
    rows,
    scalePercent: tile.scalePercent,
  };
}

/**
 * Resolves a point on the page — in the CSS pixels the layout is authored in,
 * measured from the page's top-left — to the cell under it, or null when it
 * falls outside the grid: a margin, the row-label column, or past the last row
 * of this tile. Null means "do not anchor this gesture", which is the right
 * answer: an annotation with no cell could not survive an option change.
 *
 * This is the entry point for anything measuring the *rendered* page, where a
 * pointer position is a fraction of an element's box and therefore already in
 * CSS pixels. `pdfPointToCellAnchor` is the same question asked in PDF space.
 */
export function cssPointToCellAnchor(
  layout: PageLayout,
  point: { cssX: number; cssY: number },
): CellAnchorPoint | null {
  const scale = layout.scalePercent / 100;
  const { cssX, cssY } = point;

  // Into the content area's own frame, then out of the tile's scale so the
  // offsets can be compared against unscaled column widths and row heights.
  const gridX = (cssX - layout.contentLeftPx) / scale;
  const gridY = (cssY - layout.contentTopPx) / scale;
  if (gridX < 0 || gridY < 0) return null;

  const column = layout.columns.find(
    (candidate) => gridX >= candidate.leftPx && gridX < candidate.leftPx + candidate.widthPx,
  );
  if (column === undefined) return null;

  const row = layout.rows.find(
    (candidate) => gridY >= candidate.topPx && gridY < candidate.topPx + candidate.heightPx,
  );
  if (row === undefined) return null;

  return {
    rowKey: row.key,
    columnNumber: column.number,
    originX: (gridX - column.leftPx) / column.widthPx,
    originY: (gridY - row.topPx) / row.heightPx,
  };
}

/**
 * The same resolution in PDF space, for callers holding a point as a PDF
 * viewer reports it: `pdfY` grows upward from the bottom of the page while the
 * layout grows downward from the top, hence the flip against `pageHeightPt`.
 */
export function pdfPointToCellAnchor(
  layout: PageLayout,
  point: { pdfX: number; pdfY: number },
  pageHeightPt: number,
): CellAnchorPoint | null {
  return cssPointToCellAnchor(layout, {
    cssX: point.pdfX * CSS_PX_PER_PDF_POINT,
    cssY: (pageHeightPt - point.pdfY) * CSS_PX_PER_PDF_POINT,
  });
}
